import { useCallback, useEffect, useMemo, useState } from "react";
import { $apiV6 } from "../../../../config/api";
import {
  normalizeSeasonSelectionIds,
} from "../../shared/utils/seasonSelection";

const toNumericId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getStatKey = (stat) => {
  const numericId = toNumericId(
    stat?.parameter_id ?? stat?.param_id ?? stat?.id
  );

  if (numericId !== null) {
    return `id:${numericId}`;
  }

  const fallbackName =
    stat?.parameter_name || stat?.name || stat?.title || "";

  return fallbackName ? `name:${fallbackName}` : null;
};

const normalizeMarkers = (markers) => {
  if (typeof markers === "string") {
    return markers
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (!Array.isArray(markers)) {
    return [];
  }

  return markers
    .map((item) => {
      if (item && typeof item === "object") {
        return item.id ?? null;
      }
      return item;
    })
    .filter((item) => item !== null && item !== undefined && item !== "");
};

const mergeMarkerLists = (...markerCollections) => {
  const markerSet = new Set();

  markerCollections.forEach((collection) => {
    normalizeMarkers(collection).forEach((marker) => markerSet.add(marker));
  });

  return Array.from(markerSet);
};

const mergeTeamLeaderGroups = (leaderGroupCollections = []) => {
  const mergedGroups = new Map();

  leaderGroupCollections.forEach((leaderGroups) => {
    (Array.isArray(leaderGroups) ? leaderGroups : []).forEach((group) => {
      const groupKey = getStatKey(group);
      if (!groupKey) {
        return;
      }

      const previousGroup = mergedGroups.get(groupKey) || {
        ...group,
        leaders: [],
      };

      const mergedLeadersByKey = new Map();

      [...(previousGroup.leaders || []), ...(group?.leaders || [])].forEach(
        (leader) => {
          const leaderPlayerId = toNumericId(leader?.player?.id ?? leader?.id);
          const leaderKey =
            leaderPlayerId !== null
              ? `player:${leaderPlayerId}`
              : leader?.player?.display_name || leader?.name || null;

          if (!leaderKey) {
            return;
          }

          const previousLeader = mergedLeadersByKey.get(leaderKey) || {
            ...leader,
            count: 0,
            markers: [],
          };
          const nextLeaderCount = Number(leader?.count ?? leader?.value ?? 0);
          previousLeader.count += Number.isFinite(nextLeaderCount) ? nextLeaderCount : 0;
          previousLeader.markers = mergeMarkerLists(
            previousLeader.markers,
            leader?.markers
          );

          mergedLeadersByKey.set(leaderKey, previousLeader);
        }
      );

      previousGroup.leaders = Array.from(mergedLeadersByKey.values()).sort(
        (leftLeader, rightLeader) =>
          Number(rightLeader?.count ?? 0) - Number(leftLeader?.count ?? 0)
      );

      mergedGroups.set(groupKey, previousGroup);
    });
  });

  return Array.from(mergedGroups.values()).sort(
    (leftGroup, rightGroup) =>
      Number(leftGroup?.parameter_id ?? leftGroup?.id ?? 0) -
      Number(rightGroup?.parameter_id ?? rightGroup?.id ?? 0)
  );
};

// Sums the overview stat lists (main_stats / in_attack / in_defence) across
// per-season payloads by parameter identity, so a multi-season selection shows
// combined totals instead of just the first season's values. Mirrors the
// count-summing in mergeTeamLeaderGroups.
const mergeOverviewStatLists = (statCollections = []) => {
  const mergedStats = new Map();

  statCollections.forEach((stats) => {
    (Array.isArray(stats) ? stats : []).forEach((stat) => {
      const statKey = getStatKey(stat);
      if (!statKey) {
        return;
      }

      const previousStat = mergedStats.get(statKey);
      if (!previousStat) {
        mergedStats.set(statKey, {
          ...stat,
          markers: mergeMarkerLists(stat?.markers),
        });
        return;
      }

      const nextCount =
        Number(previousStat?.count ?? previousStat?.value ?? 0) +
        Number(stat?.count ?? stat?.value ?? 0);
      previousStat.count = Number.isFinite(nextCount) ? nextCount : 0;
      previousStat.value = previousStat.count;

      const previousUniqueCount = Number(
        previousStat?.event_count ??
          previousStat?.unique_markers_count ??
          previousStat?.unique_count
      );
      const nextUniqueCount = Number(
        stat?.event_count ?? stat?.unique_markers_count ?? stat?.unique_count
      );
      if (Number.isFinite(previousUniqueCount) || Number.isFinite(nextUniqueCount)) {
        const mergedUniqueCount =
          (Number.isFinite(previousUniqueCount) ? previousUniqueCount : 0) +
          (Number.isFinite(nextUniqueCount) ? nextUniqueCount : 0);
        previousStat.event_count = mergedUniqueCount;
        previousStat.unique_count = mergedUniqueCount;
        previousStat.unique_markers_count = mergedUniqueCount;
      }

      previousStat.markers = mergeMarkerLists(
        previousStat.markers,
        stat?.markers
      );

      mergedStats.set(statKey, previousStat);
    });
  });

  return Array.from(mergedStats.values());
};

const buildMergedLeadersPayload = (payloads = []) => {
  const normalizedPayloads = payloads.filter(Boolean);
  const primaryPayload = normalizedPayloads[0] || null;
  if (!primaryPayload) {
    return null;
  }

  return {
    ...primaryPayload,
    main_stats: mergeOverviewStatLists(
      normalizedPayloads.map((payload) => payload?.main_stats)
    ),
    in_attack: mergeOverviewStatLists(
      normalizedPayloads.map((payload) => payload?.in_attack)
    ),
    in_defence: mergeOverviewStatLists(
      normalizedPayloads.map((payload) => payload?.in_defence)
    ),
    team_leaders: mergeTeamLeaderGroups(
      normalizedPayloads.map((payload) => payload?.team_leaders)
    ),
  };
};

/**
 * Fetches the Team Leaders section from the dedicated v6 endpoint. This
 * replaced the old v5 /overview call, which bundled the whole page (team_info,
 * matches, parameters, match_backed_stats) and was slower and 500-prone. Every
 * one of those bundled fields already has its own primary route now — the v1
 * team_opt shell, the filter hook's parameters fetch, and the per-match v1
 * /stats/team endpoint — so this hook is scoped to team_leaders only. It keeps
 * the { data, loading, error, resolvedSeasonIds } contract of the old hook so
 * the season-resolution plumbing on the page stays unchanged.
 */
export const useTeamLeadersData = ({ teamId, selectedSeasonIds }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvedSeasonIds, setResolvedSeasonIds] = useState([]);
  const normalizedSelectedSeasonIds = useMemo(
    () => normalizeSeasonSelectionIds(selectedSeasonIds),
    [selectedSeasonIds]
  );

  const fetch = useCallback(() => {
    if (!teamId) {
      setLoading(false);
      setData(null);
      setError(null);
      setResolvedSeasonIds([]);
      return undefined;
    }

    let isCancelled = false;
    setLoading(true);
    setResolvedSeasonIds([]);

    const fetchLeaders = async () => {
      let payloads = [];
      let requestError = null;

      try {
        if (normalizedSelectedSeasonIds.length > 0) {
          const settledResponses = await Promise.allSettled(
            normalizedSelectedSeasonIds.map((seasonId) =>
              $apiV6.get(`/platform/team/${teamId}/team_leaders?year_season_id=${seasonId}`)
            )
          );

          if (isCancelled) {
            return;
          }

          const fulfilledResponses = settledResponses
            .filter((response) => response.status === "fulfilled")
            .map((response) => response.value?.data)
            .filter(Boolean);

          const rejectedResponses = settledResponses
            .filter((response) => response.status === "rejected")
            .map((response) => response.reason)
            .filter(Boolean);

          if (fulfilledResponses.length > 0) {
            payloads = fulfilledResponses;
            if (rejectedResponses.length > 0) {
              requestError = rejectedResponses[0];
            }
          } else {
            if (rejectedResponses.length > 0) {
              requestError = rejectedResponses[0];
            }

            const fallbackResponse = await $apiV6.get(
              `/platform/team/${teamId}/team_leaders`
            );
            if (isCancelled) {
              return;
            }
            payloads = fallbackResponse?.data ? [fallbackResponse.data] : [];
          }
        } else {
          const response = await $apiV6.get(`/platform/team/${teamId}/team_leaders`);
          if (isCancelled) {
            return;
          }
          payloads = response?.data ? [response.data] : [];
        }

        const mergedPayload = buildMergedLeadersPayload(payloads);
        const mergedResolvedSeasonIds = normalizeSeasonSelectionIds(
          payloads.map((payload) => payload?.year_season_id)
        );

        if (!mergedPayload) {
          throw requestError || new Error("Team leaders payload is empty.");
        }

        if (isCancelled) {
          return;
        }

        setData(mergedPayload);
        setResolvedSeasonIds(mergedResolvedSeasonIds);
        setError(requestError);
      } catch (err) {
        if (isCancelled) {
          return;
        }
        setData(null);
        setError(err);
        setResolvedSeasonIds([]);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchLeaders();

    return () => {
      isCancelled = true;
    };
  }, [teamId, normalizedSelectedSeasonIds]);

  useEffect(() => {
    return fetch();
  }, [fetch]);

  return { data, loading, error, resolvedSeasonIds };
};
